import * as React from 'react';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { useDispatch, useSelector } from 'react-redux';

import {
  RecommendationOfferDesktopSkeleton,
  RecommendationOfferDesktopWithHover,
} from '../../components/RedesignedRecommendationOfferDesktop';
import { RedesignedRecommendationsListDesktop } from '../../components/RedesignedRecommendationsListDesktop';
import { FavoritesAssignWidgetContainer } from '../FavoritesAssignWidgetContainer';
import { RedesignedFavoriteButtonDesktopContainer } from '../RedesignedFavoriteButtonDesktopContainer';
import { RedesignedLabelsContainer } from '../RedesignedLabelsContainer';
import {
  selectRecommendationsHasMore,
  selectRecommendationsPaginationStatus,
  selectRecommendationsTotalCount,
} from '../../selectors/recommendations';
import { fetchMoreRecommendations } from '../../thunk/recommendations';
import { TThunkDispatch } from '../../types/redux';
import { EPaginationStatus } from '../../types/redux/recommendations';
import { TRecommendation } from '../../types/recommendation';
import { RedesignedGeoInformationContainer } from './RedesignedGeoInformationContainer';
import * as styles from './RedesignedRecommendationsListContainer.css';

type TRecommendationClickHandler = (offer: TRecommendation, position: number) => void;
type TTrackTeaserShowHandler = (offerId: number, position: number) => void;

const PAGINATION_SKELETON_COUNT = 8;

function usePaginationSentinel({
  hasMore,
  isLoading,
  onLoadMore,
}: {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore(): void;
}) {
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!hasMore || isLoading || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const sentinelElement = sentinelRef.current;

    if (!sentinelElement) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        rootMargin: '400px',
      },
    );

    observer.observe(sentinelElement);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, onLoadMore]);

  return sentinelRef;
}

type TRedesignedRecommendationsListContainerProps = {
  isOfferVisited(offerId: number): boolean;
  pageTypeSource: string | null;
  recommendations: TRecommendation[];
  onEntityTrackTeaserShow: TTrackTeaserShowHandler;
  onRecommendationClick: TRecommendationClickHandler;
};

export const RedesignedRecommendationsListContainer: React.FC<TRedesignedRecommendationsListContainerProps> = ({
  isOfferVisited,
  pageTypeSource,
  recommendations,
  onEntityTrackTeaserShow,
  onRecommendationClick,
}) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const hasMore = useSelector(selectRecommendationsHasMore);
  const paginationStatus = useSelector(selectRecommendationsPaginationStatus);
  const totalCount = useSelector(selectRecommendationsTotalCount);
  const isPaginationLoading = paginationStatus === EPaginationStatus.Loading;
  const showPaginationSentinel = hasMore && paginationStatus !== EPaginationStatus.Error;

  const handleLoadMore = React.useCallback(() => {
    void dispatch(fetchMoreRecommendations());
  }, [dispatch]);

  const sentinelRef = usePaginationSentinel({
    hasMore: showPaginationSentinel,
    isLoading: isPaginationLoading,
    onLoadMore: handleLoadMore,
  });

  const paginationSkeletons = React.useMemo(
    () =>
      Array.from({ length: PAGINATION_SKELETON_COUNT }, (_, index) => (
        <RecommendationOfferDesktopSkeleton key={`redesigned-recommendation-desktop-pagination-skeleton-${index}`} />
      )),
    [],
  );

  const paginationFooter = [
    ...(isPaginationLoading ? paginationSkeletons : []),
    ...(showPaginationSentinel
      ? [
          <div
            key="redesigned-recommendations-pagination-sentinel"
            ref={sentinelRef}
            className={styles['pagination-sentinel']}
          />,
        ]
      : []),
  ];

  return (
    <React.Fragment>
      <RedesignedRecommendationsListDesktop total={totalCount ?? recommendations.length} footer={paginationFooter}>
        {recommendations.map((recOffer: TRecommendation, position: number) => (
          <ActionAfterViewObserver
            key={recOffer.offerId}
            percentVisible={0.5}
            callback={() => onEntityTrackTeaserShow(recOffer.offerId, position + 1)}
            triggerOnce
            callbackDelay={1000}
          >
            <RecommendationOfferDesktopWithHover
              position={position + 1}
              geoInformation={
                <RedesignedGeoInformationContainer
                  category={recOffer.category}
                  geo={recOffer.geo}
                  regionId={recOffer.regionId}
                />
              }
              favoriteButton={
                <RedesignedFavoriteButtonDesktopContainer offerId={recOffer.offerId} position={position + 1} />
              }
              offer={recOffer}
              onClick={onRecommendationClick}
              labels={<RedesignedLabelsContainer visited={isOfferVisited(recOffer.offerId)} />}
              pageTypeSource={pageTypeSource}
            />
          </ActionAfterViewObserver>
        ))}
      </RedesignedRecommendationsListDesktop>
      <FavoritesAssignWidgetContainer />
    </React.Fragment>
  );
};
